import React, { useState } from 'react';

const PlatypusForm = ({
    formData,
    onFieldChange,
    onSubmit,
    isSubmitting,
    fields = ['name', 'email', 'phone', 'city'],
    submitText = 'Submit',
    className = '',
    layout = 'vertical',
    fieldErrors = {},
    disabled = false,
    onClick = null
}) => {
    const [dogCount, setDogCount] = useState(0);
    const [catCount, setCatCount] = useState(0);

    const getFieldConfig = (fieldName) => {
        const configs = {
            name: { placeholder: 'Your name', type: 'text', required: true, icon: 'yellowcontact.png' },
            email: { placeholder: 'Email Address', type: 'email', required: true, icon: 'yellowmail.png' },
            phone: { placeholder: 'Enter your phone', type: 'tel', required: true, icon: 'yellowphone.png' },
            city: { placeholder: 'Your city', type: 'text', required: true, icon: 'yellowmap.png' },
        };
        return configs[fieldName] || { placeholder: fieldName, type: 'text', icon: null };
    };

    const getLayoutClasses = () => {
        switch (layout) {
            case 'grid':
                return 'grid grid-cols-1 md:grid-cols-3 gap-4';
            case 'horizontal':
                return 'flex flex-col md:flex-row gap-4';
            default:
                return 'grid grid-cols-1 md:grid-cols-2 gap-4';
        }
    };

    const handleDogCountChange = (e) => {
        const value = Math.max(0, parseInt(e.target.value, 10) || 0);
        setDogCount(value);
    };

    const handleCatCountChange = (e) => {
        const value = Math.max(0, parseInt(e.target.value, 10) || 0);
        setCatCount(value);
    };

    const renderPetInput = (index, type) => {
        const nameKey = `${type}Name_${index}`;
        const ageKey = `${type}Age_${index}`;
        const label = type === 'dog' ? 'Dog' : 'Cat';

        return (
            <div     key={`${type}_${index}`} className="bg-[#2a2a2a] rounded-2xl text-white w-full max-w-full">
                <div className="flex items-center border-b border-white/20 px-4 py-3">
                    <img src="yellowpaw.png" alt="paw" className="w-[1.3rem] h-[1.3rem] mr-3" />
                    <input
                        type="text"
                        name={nameKey}
                        placeholder={`${label} ${index + 1} Name`}
                        value={formData[nameKey] || ''}
                        onChange={onFieldChange}
                        className="bg-transparent w-full focus:outline-none placeholder-gray-400 text-white"
                        required
                        disabled={isSubmitting}
                    />
                </div>
                <div className="flex items-center px-4 py-3">
                    <img src="yellowage.png" alt="age" className="w-[1.3rem] h-[1.3rem] mr-3" />
                    <input
                        type="number"
                        name={ageKey}
                        placeholder={`${label} ${index + 1} Age`}
                        value={formData[ageKey] || ''}
                        onChange={onFieldChange}
                        className="bg-transparent w-full focus:outline-none placeholder-gray-400 text-white"
                        required
                        disabled={isSubmitting}
                    />
                </div>
            </div>
        );
    };

    return (
        <form onSubmit={onSubmit} className={className}>
            {/* Main User Fields */}
            <div className={getLayoutClasses()}>
                {fields.map((fieldName) => {
                    const config = getFieldConfig(fieldName);
                    const hasError = fieldErrors[fieldName];

                    return (
                        <div key={fieldName} className="w-full">
                            <div
                                className={`w-full h-[60px] rounded-md border ${
                                    hasError ? 'border-red-400' : 'border-white/20'
                                } bg-[#2a2a2a] flex items-center px-4 py-3 text-white`}
                            >
                                {config.icon && (
                                    <img
                                        src={config.icon}
                                        alt={fieldName}
                                        className="w-[1.5rem] h-[1.5rem] mr-3"
                                    />
                                )}
                                <input
                                    type={config.type}
                                    name={fieldName}
                                    placeholder={config.placeholder}
                                    value={formData[fieldName] || ''}
                                    onChange={onFieldChange}
                                    className="bg-transparent w-full focus:outline-none placeholder-gray-400 text-white"
                                    required={config.required}
                                    disabled={isSubmitting || disabled}
                                />
                            </div>
                            {hasError && (
                                <p className="text-red-400 text-sm mt-1 ml-2">{hasError}</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Pet Info Section */}
            <div className="mt-8 border-t border-gray-600 pt-6">
                <h3 className="text-white mb-3 text-base font-medium">Tell us about your pets</h3>

                {/* Pet Counts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center h-[60px] rounded-md border border-white/20 bg-[#2a2a2a] px-4 text-white">
                        <img src="yellowdog.png" alt="Dog" className="w-[1.5rem] h-[1.5rem] mr-3" />
                        <span className="text-sm text-gray-400 mr-3">Dog </span>
                        <input
                            type="number"
                            name="dogCount"
                            min="0"
                            max="20"
                            value={dogCount}
                            onChange={handleDogCountChange}
                            placeholder="Number of Dogs"
                            className="bg-transparent w-full focus:outline-none placeholder-gray-400 text-white"
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="flex items-center h-[60px] rounded-md border border-white/20 bg-[#2a2a2a] px-4 text-white">
                        <img src="yellowcat.png" alt="Cat" className="w-[1.5rem] h-[1.5rem] mr-3" />
                        <span className="text-sm text-gray-400 mr-3">Cat </span>
                        <input
                            type="number"
                            name="catCount"
                            min="0"
                            max="20"
                            value={catCount}
                            onChange={handleCatCountChange}
                            placeholder="Number of Cats"
                            className="bg-transparent w-full focus:outline-none placeholder-gray-400 text-white"
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                {/* Dog/Cat Inputs */}
                {(dogCount > 0 || catCount > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="flex flex-col gap-4">
                            {Array.from({ length: dogCount }, (_, i) => renderPetInput(i, 'dog'))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {Array.from({ length: catCount }, (_, i) => renderPetInput(i, 'cat'))}
                        </div>
                    </div>
                )}
            </div>

            {/* Submit Button */}
            <button
                type="submit"
                disabled={isSubmitting || disabled}
                onClick={onClick}
                className="text-white bg-black border border-yellow-400 px-4 py-3 rounded-md shadow-md hover:shadow-[0_0_8px_#FFE135] transition w-full mt-4 disabled:opacity-50"
            >
                {isSubmitting ? 'Submitting...' : submitText}
            </button>
        </form>
    );
};

export default PlatypusForm;